import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Container, Row, Col, Button, Spinner, Alert, Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'
import Select from 'react-select'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDateTime, formatDateOnly, highlightText } from '../utils/commonUtils'
import { useTableState, createColumnToggleDropdown, createFilterDropdown } from '../utils/tableUtils'
import ResizableTable from '../components/ResizableTable'
import StickyScrollbar from '../components/StickyScrollbar'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import StockItemInfo from '../components/StockItemInfo'
import '../styles/selectControl.css'

const STATE_COLORS = { ENABLED: 'success', PAUSED: 'warning', ARCHIVED: 'secondary' }
const STATE_ICONS = { ENABLED: 'fa-play', PAUSED: 'fa-pause', ARCHIVED: 'fa-box-archive' }
const STRATEGY_LABELS = {
    LEGACY_FOR_SALES: 'Legacy',
    AUTO_FOR_SALES: 'Down Only',
    MANUAL: 'Fixed',
}
const STATE_MAP = { ENABLED: 'Enabled', PAUSED: 'Paused', ARCHIVED: 'Archived' }
const TARGETING_MAP = { AUTO: 'Auto', MANUAL: 'Manual' }

// When adding a new ad automation case, add an entry here and in the backend enrolled_campaigns builder (advertising.py)
const AUTOMATION_TYPES = {
    bidding: { abbr: 'IAB', label: 'Inventory-Aware Bidding', color: '#0d6efd' },
}

const initialTableState = {
    visibility: {
        image: true,
        state: true,
        name: true,
        asin: true,
        sku: true,
        stockItem: true,
        automations: true,
        targeting: true,
        budget: true,
        strategy: true,
        placementBid: true,
        startDate: true,
        endDate: true,
    },
    widths: {
        image: 'auto',
        state: 'auto',
        name: 300,
        asin: 180,
        sku: 200,
        stockItem: 250,
        automations: 'auto',
        targeting: 'auto',
        budget: 'auto',
        strategy: 'auto',
        placementBid: 'auto',
        startDate: 'auto',
        endDate: 'auto',
    },
}

const columnLabels = {
    image: 'Image',
    state: 'State',
    name: 'Campaign Name',
    asin: 'ASIN',
    sku: 'SKU',
    stockItem: 'Stock Item',
    targeting: 'Targeting',
    budget: 'Budget',
    strategy: 'Strategy',
    placementBid: 'Placement Bid',
    startDate: 'Start Date',
    endDate: 'End Date',
}

function AmazonSPCampaignsScreen() {
    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState('')
    const [error, setError] = useState('')

    const [cacheStatus, setCacheStatus] = useState(null)
    const [cachedData, setCachedData] = useState(null)
    const [loadingData, setLoadingData] = useState(false)
    const [syncing, setSyncing] = useState(false)
    const pollIntervalRef = useRef(null)

    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)

    const [stateFilter, setStateFilter] = useState('')
    const [targetingFilter, setTargetingFilter] = useState('')
    const [strategyFilter, setStrategyFilter] = useState('')

    const { columnState, updateColumnVisibility } = useTableState('amazon_sp_campaigns', initialTableState)
    const columnVisibility = columnState.visibility

    const toggleColumnVisibility = (columnId) => {
        updateColumnVisibility(columnId, !columnVisibility[columnId])
    }

    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                const active = (data || []).filter(a => a.is_active)
                setAccounts(active)
                if (active.length === 1) setSelectedAccountId(active[0].id)
            } catch {
                setError('Failed to load accounts')
            }
        }
        fetchAccounts()
    }, [])

    useEffect(() => {
        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
        }
    }, [])

    const loadCacheStatus = useCallback(async (accountId) => {
        try {
            const status = await amazonAPI.advertising.getSpCacheStatus(accountId)
            setCacheStatus(status)
            if (status?.sync_status === 'SYNCING') {
                setSyncing(true)
                startPolling(accountId)
            }
            return status
        } catch (err) {
            setError(formatErrorResponse(err))
            return null
        }
    }, [])

    const loadCachedData = useCallback(async (accountId) => {
        setLoadingData(true)
        try {
            const data = await amazonAPI.advertising.getSpCacheData(accountId)
            setCachedData(data)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoadingData(false)
        }
    }, [])

    useEffect(() => {
        if (!selectedAccountId) {
            setCacheStatus(null)
            setCachedData(null)
            return
        }
        loadCacheStatus(selectedAccountId)
        loadCachedData(selectedAccountId)
    }, [selectedAccountId])

    const startPolling = (accountId) => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = setInterval(async () => {
            try {
                const status = await amazonAPI.advertising.getSpCacheStatus(accountId)
                setCacheStatus(status)
                if (status.sync_status !== 'SYNCING') {
                    clearInterval(pollIntervalRef.current)
                    pollIntervalRef.current = null
                    setSyncing(false)
                    if (status.sync_status === 'COMPLETED') {
                        loadCachedData(accountId)
                    }
                }
            } catch {
                clearInterval(pollIntervalRef.current)
                pollIntervalRef.current = null
                setSyncing(false)
            }
        }, 3000)
    }

    const handleSync = async () => {
        if (!selectedAccountId) return
        setError('')
        setSyncing(true)
        try {
            await amazonAPI.advertising.triggerSpCacheSync(selectedAccountId)
            startPolling(selectedAccountId)
        } catch (err) {
            setSyncing(false)
            setError(formatErrorResponse(err))
        }
    }

    const enrichedCampaigns = useMemo(() => {
        if (!cachedData?.campaigns) return []

        const productAds = cachedData.productAds || []
        const stockItems = cachedData.stockItems || {}
        const enrolledCampaigns = cachedData.enrolled_campaigns || {}

        const campaignAsinMap = {}
        const campaignSkuMap = {}
        for (const pa of productAds) {
            const cid = pa.campaignId
            if (!cid) continue
            if (pa.asin && !campaignAsinMap[cid]?.includes(pa.asin)) {
                if (!campaignAsinMap[cid]) campaignAsinMap[cid] = []
                campaignAsinMap[cid].push(pa.asin)
            }
            if (pa.sku && !campaignSkuMap[cid]?.includes(pa.sku)) {
                if (!campaignSkuMap[cid]) campaignSkuMap[cid] = []
                campaignSkuMap[cid].push(pa.sku)
            }
        }

        return cachedData.campaigns.map(c => {
            const asins = campaignAsinMap[c.campaignId] || []
            const skus = campaignSkuMap[c.campaignId] || []
            const primaryAsin = asins[0] || ''
            const stockItem = primaryAsin ? (stockItems[primaryAsin] || null) : null
            const topPlacement = (c.dynamicBidding?.placementBidding || [])
                .find(p => p.placement === 'PLACEMENT_TOP')
            return {
                ...c,
                primaryAsin,
                asinCount: asins.length,
                primarySku: skus[0] || '',
                skuCount: skus.length,
                stockItem,
                stockItemDescription: stockItem?.description || '',
                topPlacementPct: topPlacement?.percentage ?? null,
                enrollments: enrolledCampaigns[c.campaignId] || [],
            }
        })
    }, [cachedData])

    const badgeStats = useMemo(() => {
        let enabled = 0, paused = 0, archived = 0, activeBudget = 0
        const asinSet = new Set()
        for (const c of enrichedCampaigns) {
            if (c.state === 'ENABLED') { enabled++; activeBudget += (c.budget?.budget || 0) }
            else if (c.state === 'PAUSED') paused++
            else if (c.state === 'ARCHIVED') archived++
            if (c.primaryAsin) asinSet.add(c.primaryAsin)
        }
        return { enabled, paused, archived, activeBudget, uniqueAsins: asinSet.size }
    }, [enrichedCampaigns])

    const filteredCampaigns = useMemo(() => {
        let result = enrichedCampaigns

        if (stateFilter) {
            result = result.filter(c => c.state === stateFilter)
        }
        if (targetingFilter) {
            result = result.filter(c => c.targetingType === targetingFilter)
        }
        if (strategyFilter) {
            result = result.filter(c => c.dynamicBidding?.strategy === strategyFilter)
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase()
            result = result.filter(c =>
                (c.name && c.name.toLowerCase().includes(term)) ||
                (c.primaryAsin && c.primaryAsin.toLowerCase().includes(term)) ||
                (c.stockItemDescription && c.stockItemDescription.toLowerCase().includes(term))
            )
        }

        if (sortColumn && sortDirection) {
            result = [...result].sort((a, b) => {
                let aVal, bVal
                switch (sortColumn) {
                    case 'name':
                        aVal = (a.name || '').toLowerCase()
                        bVal = (b.name || '').toLowerCase()
                        break
                    case 'budget':
                        aVal = a.budget?.budget || 0
                        bVal = b.budget?.budget || 0
                        break
                    case 'startDate':
                        aVal = a.startDate || ''
                        bVal = b.startDate || ''
                        break
                    case 'endDate':
                        aVal = a.endDate || ''
                        bVal = b.endDate || ''
                        break
                    default:
                        return 0
                }
                if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1
                if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1
                return 0
            })
        }

        return result
    }, [enrichedCampaigns, stateFilter, targetingFilter, strategyFilter, searchTerm, sortColumn, sortDirection])

    const totalItems = filteredCampaigns.length
    const startIdx = (currentPage - 1) * pageSize
    const displayedCampaigns = filteredCampaigns.slice(startIdx, startIdx + pageSize)

    useEffect(() => {
        setCurrentPage(1)
    }, [stateFilter, targetingFilter, strategyFilter, searchTerm])

    const handleSearchInputChange = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
        }, 500)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
    }

    const handleSort = (colId, direction) => {
        setSortColumn(colId)
        setSortDirection(direction)
    }


    const columns = [
        columnVisibility.image && {
            id: 'image',
            header: 'Image',
            cell: (row) => row.stockItem?.image ? (
                <OverlayTrigger
                    placement="right"
                    overlay={
                        <Tooltip className="stock-item-tooltip">
                            <StockItemInfo stockItem={row.stockItem} />
                        </Tooltip>
                    }
                >
                    <img
                        src={row.stockItem.image}
                        alt="Stock Item Thumbnail"
                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                    />
                </OverlayTrigger>
            ) : null,
        },
        columnVisibility.state && {
            id: 'state',
            header: () => createFilterDropdown({
                selectedValue: stateFilter,
                handleSelect: (value) => setStateFilter(value),
                items: STATE_MAP,
                defaultLabel: 'State',
            }),
            cell: (row) => (
                <div className="text-center">
                    <i className={`fas ${STATE_ICONS[row.state]} text-${STATE_COLORS[row.state] || 'secondary'}`} title={STATE_MAP[row.state] || row.state}></i>
                </div>
            ),
        },
        columnVisibility.name && {
            id: 'name',
            header: 'Campaign Name',
            sortable: true,
            cell: (row) => highlightText(row.name, searchTerm),
        },
        columnVisibility.asin && {
            id: 'asin',
            header: 'ASIN',
            cell: (row) => row.primaryAsin ? (
                <>
                    <CopyableText value={row.primaryAsin} toastLabel="ASIN" searchTerm={searchTerm} />
                    {row.asinCount > 1 && (
                        <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>+{row.asinCount - 1}</Badge>
                    )}
                </>
            ) : '',
        },
        columnVisibility.sku && {
            id: 'sku',
            header: 'SKU',
            cell: (row) => row.primarySku ? (
                <>
                    <CopyableText value={row.primarySku} toastLabel="SKU" searchTerm={searchTerm} />
                    {row.skuCount > 1 && (
                        <Badge bg="info" className="ms-1" style={{ fontSize: '0.65rem' }}>+{row.skuCount - 1}</Badge>
                    )}
                </>
            ) : '',
        },
        columnVisibility.stockItem && {
            id: 'stockItem',
            header: 'Stock Item',
            cell: (row) => row.stockItemDescription ? highlightText(row.stockItemDescription, searchTerm) : '',
        },
        columnVisibility.automations && {
            id: 'automations',
            header: 'Automations',
            cell: (row) => row.enrollments.length > 0 ? (
                <div className="d-flex gap-1 justify-content-center">
                    {row.enrollments.map((e, idx) => {
                        const auto = AUTOMATION_TYPES[e.type]
                        if (!auto) return null
                        return (
                            <OverlayTrigger
                                key={idx}
                                placement="top"
                                overlay={<Tooltip>{auto.label}</Tooltip>}
                            >
                                <span
                                    style={{
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: '#fff',
                                        background: auto.color,
                                        borderRadius: '3px',
                                        padding: '1px 4px',
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    {auto.abbr}
                                </span>
                            </OverlayTrigger>
                        )
                    })}
                </div>
            ) : null,
        },
        columnVisibility.targeting && {
            id: 'targeting',
            header: () => createFilterDropdown({
                selectedValue: targetingFilter,
                handleSelect: (value) => setTargetingFilter(value),
                items: TARGETING_MAP,
                defaultLabel: 'Targeting',
            }),
            cell: (row) => (
                <div className="text-center">
                    <Badge bg={row.targetingType === 'AUTO' ? 'success' : 'primary'} style={{ fontSize: '0.7rem' }} title={row.targetingType === 'AUTO' ? 'Auto' : 'Manual'}>
                        {row.targetingType === 'AUTO' ? 'A' : 'M'}
                    </Badge>
                </div>
            ),
        },
        columnVisibility.budget && {
            id: 'budget',
            header: 'Budget',
            sortable: true,
            cell: (row) => {
                const budget = row.budget?.budget
                if (budget == null) return ''
                const type = row.budget?.budgetType === 'DAILY' ? 'day' : (row.budget?.budgetType || '')
                return `$${budget}/${type}`
            },
        },
        columnVisibility.strategy && {
            id: 'strategy',
            header: () => createFilterDropdown({
                selectedValue: strategyFilter,
                handleSelect: (value) => setStrategyFilter(value),
                items: STRATEGY_LABELS,
                defaultLabel: 'Strategy',
            }),
            cell: (row) => STRATEGY_LABELS[row.dynamicBidding?.strategy] || row.dynamicBidding?.strategy || '',
        },
        columnVisibility.placementBid && {
            id: 'placementBid',
            header: 'Placement Bid',
            cell: (row) => row.topPlacementPct != null ? `+${row.topPlacementPct}%` : '',
        },
        columnVisibility.startDate && {
            id: 'startDate',
            header: 'Start Date',
            sortable: true,
            cell: (row) => formatDateOnly(row.startDate),
        },
        columnVisibility.endDate && {
            id: 'endDate',
            header: 'End Date',
            sortable: true,
            cell: (row) => formatDateOnly(row.endDate),
        },
    ].filter(Boolean)

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">SP Campaigns</h1>

            <Row className="mb-3 align-items-end">
                <Col md={3}>
                    <span className="form-label fw-bold d-block mb-1">Account</span>
                    <Select
                        value={accountOptions.find(o => o.value === selectedAccountId) || null}
                        onChange={(opt) => setSelectedAccountId(opt?.value || '')}
                        options={accountOptions}
                        placeholder="Select Account..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Col>
                <Col>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchInputChange}
                        onClear={handleClearSearch}
                        placeholder="Search by Campaign Name, ASIN, or Stock Item..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                <Col md="auto">
                    <Button
                        variant="primary"
                        onClick={handleSync}
                        disabled={syncing || !selectedAccountId}
                    >
                        {syncing ? (
                            <><Spinner animation="border" size="sm" className="me-1" />Syncing...</>
                        ) : (
                            <><i className="fas fa-sync me-1"></i>Sync from Amazon</>
                        )}
                    </Button>
                </Col>
            </Row>
            {selectedAccountId && cacheStatus?.synced_at && (
                <div className="d-flex align-items-center justify-content-end gap-1" style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)', marginTop: '-0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{
                        width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block',
                        background: cacheStatus.is_expired ? 'var(--bs-warning)' : 'var(--bs-success)',
                    }}></span>
                    {cacheStatus.is_expired ? 'Last sync' : 'Synced'} {formatDateTime(cacheStatus.synced_at)}
                </div>
            )}

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            {cacheStatus?.sync_status === 'FAILED' && cacheStatus.sync_error && (
                <Alert variant="warning" dismissible onClose={() => {}}>
                    Last sync failed: {cacheStatus.sync_error}
                </Alert>
            )}

            {selectedAccountId && cacheStatus?.synced_at && (
                <Row className="mb-4 g-3">
                    <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Enabled</h5><h3 className="mb-0">{badgeStats.enabled.toLocaleString()}</h3></div></Col>
                    <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Paused</h5><h3 className="mb-0">{badgeStats.paused.toLocaleString()}</h3></div></Col>
                    <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Archived</h5><h3 className="mb-0">{badgeStats.archived.toLocaleString()}</h3></div></Col>
                    <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Active Daily Budget</h5><h3 className="mb-0">${badgeStats.activeBudget.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3></div></Col>
                    <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Unique ASINs</h5><h3 className="mb-0">{badgeStats.uniqueAsins.toLocaleString()}</h3></div></Col>
                </Row>
            )}

            {loadingData ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : cachedData?.from_cache ? (
                <>
                    <div className="mb-2">
                        {createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}
                    </div>
                    <StickyScrollbar>
                        <ResizableTable
                            columns={columns}
                            data={displayedCampaigns}
                            enableSorting={true}
                            onSort={handleSort}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            initialColumnWidths={columnState.widths}
                            tableId="amazon_sp_campaigns"
                            striped={true}
                            hover={true}
                            className="table-sm"
                        />
                    </StickyScrollbar>
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalItems}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            ) : selectedAccountId ? (
                <div className="text-center text-muted py-4">No cached data. Click Sync from Amazon to fetch.</div>
            ) : null}
        </Container>
    )
}

export default AmazonSPCampaignsScreen
